import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import BookCard from "@/components/BookCard";
import { applyPageSeo } from "@/lib/seo";
import { X, Filter, ChevronDown } from "lucide-react";

const SORTS = [
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "title", label: "Title A–Z" },
];

export default function Books() {
  const [params, setParams] = useSearchParams();
  const [facets, setFacets] = useState({ categories: [], subjects: [], class_levels: [], publishers: [] });
  const [books, setBooks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const subject = params.get("subject") || "";
  const class_level = params.get("class_level") || "";
  const publisher = params.get("publisher") || "";
  const sort = params.get("sort") || "newest";
  const new_arrival = params.get("new_arrival") === "true";
  const featured = params.get("featured") === "true";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/books", {
        params: {
          q: q || undefined,
          category: category || undefined,
          subject: subject || undefined,
          class_level: class_level || undefined,
          publisher: publisher || undefined,
          new_arrival: new_arrival || undefined,
          featured: featured || undefined,
          sort,
          limit: 60,
        },
      });
      setBooks(data.items || []);
      setTotal(data.total || 0);
    } catch {
      setBooks([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q, category, subject, class_level, publisher, sort, new_arrival, featured]);

  useEffect(() => { load(); }, [load]);

  // ----- SEO: hoist category / subject / publisher into the title & canonical
  // so "Art Books", "History Books", "Buddhism Books" etc. rank as primary
  // keywords, and every filtered listing has its own canonical URL.
  useEffect(() => {
    const kw = category || subject || publisher || q;
    const label = kw ? `${kw} Books` : "All Books";
    const title = kw
      ? `${label} — Buy ${kw} Books Online | GAINA THE PRELUDE`
      : `Books — Scholarly, Rare & Academic Titles | GAINA THE PRELUDE`;
    const description = kw
      ? `Shop ${label} at GAINA THE PRELUDE — a specialised Books Distributor for universities, libraries, museums and readers worldwide. Free shipping across India, worldwide delivery on request.`
      : `Browse the complete GAINA THE PRELUDE catalogue — scholarly, rare, coffee-table, children, law and Hindi literature. Trusted by universities, libraries, museums and corporates.`;
    // Rebuild canonical from the active query so /books?category=Art has its
    // own indexable URL. Only include filters that materially change the list.
    const qs = new URLSearchParams();
    if (category) qs.set("category", category);
    else if (subject) qs.set("subject", subject);
    else if (publisher) qs.set("publisher", publisher);
    else if (q) qs.set("q", q);
    const path = `/books${qs.toString() ? `?${qs.toString()}` : ""}`;
    applyPageSeo({
      title,
      description,
      keywords: [kw, "GAINA THE PRELUDE", "books distributor", "buy books online", "Indian bookstore", "library supplier"].filter(Boolean).join(", "),
      path,
    });
  }, [category, subject, publisher, q]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/books/facets");
        setFacets(data);
      } catch (e) {
        console.error("facets load failed", e);
      }
    })();
  }, []);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v) next.delete(k);
    else next.set(k, v);
    setParams(next, { replace: true });
  };

  const clearAll = () => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    setParams(next, { replace: true });
  };

  const activeFilters = [
    ["category", category],
    ["subject", subject],
    ["class_level", class_level],
    ["publisher", publisher],
  ].filter(([, v]) => v);

  const Sidebar = () => (
    <aside className="w-full md:w-64 shrink-0 border border-border bg-white" data-testid="filter-sidebar">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="font-display font-black tracking-tight">Filters</div>
        <button onClick={clearAll} className="text-xs font-bold text-[#ea580c] hover:underline" data-testid="clear-filters">Clear all</button>
      </div>
      <FacetGroup label="Category" items={facets.categories} value={category} onChange={(v) => setFilter("category", v)} testid="facet-category" />
    </aside>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="books-page">
      <div className="mb-6 border-b-2 border-[#09090b] pb-4 flex items-end justify-between">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c]">Catalogue</div>
          <h1 className="font-display text-3xl md:text-5xl font-black tracking-tight">
            {q ? `Results for "${q}"` : "All Books"}
          </h1>
          <div className="text-sm text-[#52525b] mt-1" data-testid="results-count">{total} book{total !== 1 ? "s" : ""}</div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={sort}
            onChange={(e) => setFilter("sort", e.target.value)}
            className="border-2 border-[#09090b] bg-white px-3 py-2 text-sm font-semibold outline-none"
            data-testid="sort-select"
          >
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button
            className="md:hidden border-2 border-[#09090b] px-3 py-2 text-sm font-bold flex items-center gap-1 hidden"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-filter-toggle"
          >
            <Filter className="w-4 h-4" /> Filters
          </button>
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="active-filters">
          {activeFilters.map(([k, v]) => (
            <button key={k} onClick={() => setFilter(k, "")} className="inline-flex items-center gap-1 border border-[#09090b] px-3 py-1 text-xs font-bold hover:bg-[#09090b] hover:text-white">
              {v} <X className="w-3 h-3" />
            </button>
          ))}
        </div>
      )}

      <div>
        <div className="flex-1">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array(8).fill(0).map((_, i) => (
                <div key={i} className="border border-border bg-[#f4f4f5] aspect-[3/5] animate-pulse" />
              ))}
            </div>
          ) : books.length === 0 ? (
            <div className="border-2 border-dashed border-border p-16 text-center">
              <div className="font-display text-2xl font-black mb-2">No books found</div>
              <div className="text-sm text-[#52525b]">Try adjusting your search or filters.</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="books-grid">
              {books.map((b) => <BookCard key={b.id} book={b} />)}
            </div>
          )}
        </div>
      </div>

      {/* Mobile sidebar drawer */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 md:hidden" onClick={() => setSidebarOpen(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-80 max-w-full bg-white overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="font-display font-black">Filters</div>
              <button onClick={() => setSidebarOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <Sidebar />
          </div>
        </div>
      )}
    </div>
  );
}

function FacetGroup({ label, items, value, onChange, testid }) {
  const [open, setOpen] = useState(true);
  if (!items || items.length === 0) return null;
  return (
    <div className="border-b border-border" data-testid={testid}>
      <button
        className="w-full flex items-center justify-between px-5 py-3 font-display font-bold text-sm hover:bg-[#f4f4f5]"
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 max-h-56 overflow-y-auto space-y-1.5">
          {items.map((it) => (
            <label key={it} className="flex items-center gap-2 text-sm cursor-pointer hover:text-[#ea580c]">
              <input
                type="radio"
                name={label}
                checked={value === it}
                onChange={() => onChange(it)}
                className="accent-[#ea580c]"
              />
              <span className="truncate">{it}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
